// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";

import { setClipboardText } from "@hardwoods/App/Widgets/DesignSystem/DesignSystem.utils";
import colorPreStyle from "@hardwoods/XDL/PreStyle/colors";
import { color } from "@hardwoods/XDL/Utilities";
import { xGap } from "@hardwoods/XDL/Utilities/SharedStyles";
import translate from "@insite/client-framework/Translate";
import { Option } from "@insite/client-framework/Types/FieldDefinition";
import { ToastProps } from "@insite/mobius/Toast";
import Toaster from "@insite/mobius/Toast/Toaster";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { get } from "@insite/mobius/utilities";
import React from "react";
import { css } from "styled-components";

const style = {
    container: {
        css: css`
            display: flex;
            ${xGap("20px")}
            flex-wrap: wrap;
            justify-content: center;
            padding: 15px;
        `,
    },
    codeSnippet: {
        css: css`
            background: #f4f4f4;
            border: 1px solid #ddd;
            border-left: 3px solid #f36d33;
            color: #666;
            page-break-inside: avoid;
            font-family: monospace;
            font-size: 15px;
            line-height: 1.6;
            max-width: 100%;
            overflow: auto;
            padding: 1em 1.5em;
            display: block;
            word-wrap: break-word;
        `,
    },
    colorWrapper: {
        container: {
            css: css`
                text-align: center;
                border-radius: 8px;
                padding: 15px;
                border-radius: 8px;
                border: 1px solid ${color("common.border")};
                overflow-wrap: break-word;
                width: 200px;
                height: 200px;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
            `,
        },
        title: {
            size: 14,
            color: "common.white",
            css: css`
                display: block;
                background: ${color("text.main", 0.6)};
                padding: 2px 20px;
                border-radius: 8px;
                border: 1px solid ${color("text.main", 0.6)};
            `,
        },
        subTitle: {
            size: 12,
            color: "common.white",
            css: css`
                display: block;
                background: ${color("text.main", 0.6)};
                padding: 2px 20px;
                margin-top: 8px;
                border: 1px solid ${color("text.main", 0.6)};
                border-radius: 8px;
            `,
        },
    },
};

const ColorElement = ({
    backgroundColor,
    name,
    addToast,
}: {
    backgroundColor: any;
    name: any;
    addToast: (toastProps: ToastProps) => void;
}) => {
    return (
        <Clickable
            onClick={() => {
                const isCopied = setClipboardText(name);
                addToast({
                    body: isCopied ? `${name} color is copied` : `copy ${name} color failed, do it manually`,
                    messageType: isCopied ? "success" : "danger",
                });
            }}
        >
            <StyledWrapper
                {...style.colorWrapper.container}
                css={css`
                       ${style.colorWrapper.container.css}
                       background: ${color(backgroundColor ?? "")}
                       `}
            >
                <Typography {...style.colorWrapper.title}>{name}</Typography>
                <Typography {...style.colorWrapper.subTitle}>{get(colorPreStyle, backgroundColor)}</Typography>
            </StyledWrapper>
        </Clickable>
    );
};

function getAllColorsAsDropdownOptions(colors: any): Option<string>[] {
    const options: Option<string>[] = [];
    for (const key in colors) {
        for (const subkey in colors[key]) {
            if (subkey.toLowerCase().indexOf("contrast") === -1) {
                if (typeof get(colors, `${key}.${subkey}`) === "object") {
                    for (const subkey2 in get(colors, `${key}.${subkey}`) as object) {
                        options.push({
                            displayName: `${key}.${subkey}.${subkey2}`,
                            value: `${key}.${subkey}.${subkey2}`,
                        });
                    }
                } else {
                    options.push({ displayName: `${key}.${subkey}`, value: `${key}.${subkey}` });
                }
            }
        }
    }
    return options;
}

export const XdlColors = () => {
    return (
        <>
            <Toaster position="bottom" mobilePosition="bottom">
                <ToasterContext.Consumer>
                    {({ addToast }) => (
                        <>
                            <Clickable
                                css={css`
                                    margin-left: 40px;
                                    margin-bottom: 15px;
                                `}
                            >
                                <Typography {...style.codeSnippet}>
                                    {translate("Click on the item for copy color path")}
                                </Typography>
                            </Clickable>

                            <StyledWrapper {...style.container}>
                                {getAllColorsAsDropdownOptions(colorPreStyle).map((colorItem, index) => (
                                    <ColorElement
                                        key={`color_${colorItem.value}`}
                                        backgroundColor={colorItem.value}
                                        name={colorItem.displayName ?? ""}
                                        addToast={addToast}
                                    />
                                ))}
                            </StyledWrapper>
                        </>
                    )}
                </ToasterContext.Consumer>
            </Toaster>
        </>
    );
};
